import { useCallback, useEffect, useRef, useState } from "react";
import {
  addFavorite,
  fetchFavorites,
  removeFavorite,
  type TobisFavorite,
  type TobisFavoriteKind,
} from "./tobis-favorites";
import { TobisNotConfiguredError } from "./tobis";
import { logApplicationFailure } from "./user-error";

// Ein Hook fuer beide Aufgaben der Merkliste: die Zeile auf der Startseite
// (favorites) und den Stern auf den Detailseiten (isFavorite/toggle).
//
// Bewusst OHNE globalen Store oder Context: Zeile und Stern sind nie gleichzeitig
// am Bildschirm (Startseite vs. Detailseite), also gibt es keinen Zustand, den
// zwei Stellen synchron halten muessten. Jede Montage laedt die Liste einmal -
// dasselbe Muster wie useTobisHome in HomePage.tsx, und ein Aufruf, der ein
// paar Zeilen liest. Ein Store waere Mechanik fuer ein Problem, das der
// Prototyp nicht hat; wenn der Stern spaeter in Listen mit vielen Zeilen
// wandert, ist das der Moment fuer einen gemeinsamen Cache.

const schluessel = (kind: TobisFavoriteKind, refId: string): string => `${kind}:${refId}`;

export interface TobisFavoritesApi {
  /** Merkliste mit Anzeigedaten, neueste zuerst. null = laedt noch oder kein Prototyp angeschlossen. */
  favorites: TobisFavorite[] | null;
  isFavorite: (kind: TobisFavoriteKind, refId: string) => boolean;
  /**
   * Markierung umschalten. Optimistisch: der Stern reagiert sofort, ein
   * Serverfehler nimmt ihn zurueck.
   */
  toggle: (kind: TobisFavoriteKind, refId: string) => void;
}

export function useTobisFavorites(aktiv = true): TobisFavoritesApi {
  const [favorites, setFavorites] = useState<TobisFavorite[] | null>(null);
  const [markiert, setMarkiert] = useState<ReadonlySet<string>>(new Set());

  // Spiegel des Zustands fuer toggle(): der Callback darf nicht von `markiert`
  // abhaengen, sonst zeigt er nach einem Klick auf einen veralteten Stand
  // (Stale Closure) - und mit `markiert` in den Abhaengigkeiten waere jeder
  // Klick ausserdem eine neue Funktionsidentitaet fuer jeden Stern.
  const markiertRef = useRef<ReadonlySet<string>>(markiert);
  const setzeMarkiert = useCallback((next: ReadonlySet<string>) => {
    markiertRef.current = next;
    setMarkiert(next);
  }, []);

  useEffect(() => {
    if (!aktiv) return undefined;
    const ac = new AbortController();
    fetchFavorites(ac.signal)
      .then((liste) => {
        if (ac.signal.aborted) return;
        setFavorites(liste);
        markiertRef.current = new Set(liste.map((f) => schluessel(f.kind, f.refId)));
        setMarkiert(markiertRef.current);
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) return;
        // Ohne angeschlossene tobis-Datenbank ist die leere Merkliste die
        // richtige Antwort, kein Fehler - wie bei der Tobis-Zeile selbst.
        if (!(err instanceof TobisNotConfiguredError)) logApplicationFailure("tobis.favorites");
      });
    return () => ac.abort();
  }, [aktiv]);

  const isFavorite = useCallback(
    (kind: TobisFavoriteKind, refId: string) => markiert.has(schluessel(kind, refId)),
    [markiert],
  );

  const toggle = useCallback(
    (kind: TobisFavoriteKind, refId: string) => {
      const k = schluessel(kind, refId);
      const vorher = markiertRef.current;
      const entfernen = vorher.has(k);

      const optimistisch = new Set(vorher);
      if (entfernen) optimistisch.delete(k);
      else optimistisch.add(k);
      setzeMarkiert(optimistisch);

      // Beim Entfernen verschwindet die Kachel sofort aus der Zeile. Beim
      // Markieren wird NICHT eingefuegt: die Anzeigedaten (Name, Poster) kennt
      // nur der Server, und markiert wird auf der Detailseite - dort ist die
      // Zeile gar nicht sichtbar. Beim naechsten Besuch der Startseite laedt
      // sie neu.
      if (entfernen) {
        setFavorites((liste) =>
          liste === null ? liste : liste.filter((f) => schluessel(f.kind, f.refId) !== k),
        );
      }

      const zurueck = (err: unknown) => {
        if (!(err instanceof TobisNotConfiguredError)) logApplicationFailure("tobis.favorites");
        setzeMarkiert(vorher);
        // Die entfernte Kachel kommt beim naechsten Laden zurueck; sie hier
        // wieder an die richtige Stelle zu sortieren waere Buchhaltung fuer
        // einen Fall, den nur ein Serverfehler ausloest.
      };

      if (entfernen) void removeFavorite(kind, refId).catch(zurueck);
      else void addFavorite(kind, refId).catch(zurueck);
    },
    [setzeMarkiert],
  );

  return { favorites, isFavorite, toggle };
}
